import React, { useEffect, useState } from 'react';
import { collection, addDoc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const eventImages = Array.from({ length: 30 }, (_, index) => {
  const imageNumber = index + 1;
  return {
    src: `/carousel_images/insta${imageNumber}.jpeg`,
    title: `Event image ${imageNumber}`,
    caption: 'RushPutt event highlights from past venues and parties.',
  };
});

export default function ReviewsAndGallery() {
  const [reviews, setReviews] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [form, setForm] = useState({ name: '', rating: 5, comment: '' });
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch reviews from Firestore on component mount
  useEffect(() => {
    const q = query(collection(db, 'reviews'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const firestoreReviews = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Update reviews from Firestore
      setReviews(firestoreReviews);
    }, (error) => {
      console.error('Error fetching reviews:', error);
      // If there's an error, show empty reviews
      setReviews([]);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % eventImages.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.comment.trim()) {
      setFeedback('Please add your name and a short review before sharing.');
      return;
    }

    setIsSubmitting(true);
    setFeedback('');

    try {
      // Add review to Firestore
      const docRef = await addDoc(collection(db, 'reviews'), {
        name: form.name.trim(),
        rating: Number(form.rating),
        comment: form.comment.trim(),
        createdAt: new Date(),
      });

      // Add review to local state immediately for instant display
      const newReview = {
        id: docRef.id,
        name: form.name.trim(),
        rating: Number(form.rating),
        comment: form.comment.trim(),
        createdAt: new Date(),
      };

      setReviews((current) => [newReview, ...current]);
      setForm({ name: '', rating: 5, comment: '' });
      setFeedback('Thanks for sharing your review.');
    } catch (error) {
      console.error('Error adding review:', error);
      setFeedback('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="reviews-gallery-section">
      <div className="container">
        <div className="section-label">Community feedback</div>
        <h2 className="section-title">
          Customer <span>Reviews</span>
        </h2>
        <p className="reviews-gallery-intro">
          Share your experience and browse highlights from our previous events.
        </p>

        <div className="reviews-gallery-grid">
          <div className="review-panel">
            <form className="review-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <label htmlFor="name">Your name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Alex"
                />
              </div>

              <div className="form-row">
                <label>Rating</label>
                <div className="star-rating">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={`star ${value <= form.rating ? 'filled' : ''}`}
                      onClick={() => setForm((current) => ({ ...current, rating: value }))}
                      aria-label={`${value} star${value === 1 ? '' : 's'}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <label htmlFor="comment">Review</label>
                <textarea
                  id="comment"
                  name="comment"
                  rows="4"
                  value={form.comment}
                  onChange={handleChange}
                  placeholder="Tell us how the event felt for you..."
                />
              </div>

              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
              {feedback ? <p className="form-feedback">{feedback}</p> : null}
            </form>

            <div className="review-list">
              {reviews.map((review) => (
                <article key={review.id} className="review-card">
                  <div className="review-card-top">
                    <strong>{review.name}</strong>
                    <span>{'★'.repeat(review.rating)}</span>
                  </div>
                  <p>{review.comment}</p>
                </article>
              ))}
            </div>
          </div>

          <div className="gallery-panel">
            <div className="gallery-frame">
              <img src={eventImages[activeIndex].src} alt={eventImages[activeIndex].title} />
              <div className="gallery-caption">
                <h3>{eventImages[activeIndex].title}</h3>
                <p>{eventImages[activeIndex].caption}</p>
              </div>
            </div>

            <div className="gallery-controls">
              <button
                type="button"
                className="gallery-btn"
                onClick={() => setActiveIndex((current) => (current - 1 + eventImages.length) % eventImages.length)}
                aria-label="Show previous image"
              >
                ←
              </button>
              <div className="gallery-dots">
                {eventImages.map((image, index) => (
                  <button
                    key={image.title}
                    type="button"
                    className={`gallery-dot ${index === activeIndex ? 'active' : ''}`}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Show ${image.title}`}
                  />
                ))}
              </div>
              <button
                type="button"
                className="gallery-btn"
                onClick={() => setActiveIndex((current) => (current + 1) % eventImages.length)}
                aria-label="Show next image"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
